import React, { useMemo } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import ScheduleSettings from "./ScheduleSettings";
import FastAPI from "../../../api/FastAPI";
import useFASTLayout from "../../../hooks/useFASTLayout";
import SaveButton from "../../form/SaveButton";

import type { JSX } from "react";
import type { SubmitHandler } from "react-hook-form";

import type { Schedule, UpdatedSchedule } from "../../../api/FastAPI.types";
import type { SchedulesSettingsFieldValues } from "../../../types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

interface Props {
  scheduleList: Schedule[];
}

export default function SchedulingSettingsForm({
  scheduleList,
}: Props): JSX.Element {
  const { t } = useTranslation("common");
  const formId = "scheduling-settings-form";

  useFASTLayout({
    title: t("schedule-settings", { ns: "editor-fast-channel" }),
    button: <SaveButton formId={formId} />,
  });

  const secondsToHours = (seconds: number): number => seconds / 3600 || 0;

  const schedulesById = useMemo(
    () =>
      scheduleList.reduce((acc: SchedulesSettingsFieldValues, schedule) => {
        acc[schedule.uuid] = {
          ...schedule,
          daily_ads_objective: secondsToHours(schedule.daily_ads_objective),
          daily_programs_objective: secondsToHours(
            schedule.daily_programs_objective,
          ),
          broadcastSelectList: schedule.broadcasts.map((broadcast) => ({
            value: broadcast.broadcaster_name,
            label: broadcast.broadcaster_name,
            logoUrl: broadcast.broadcaster_logo_url,
          })),
        };

        return acc;
      }, {}),
    [scheduleList],
  );

  const methods = useForm<SchedulesSettingsFieldValues>({
    values: schedulesById,
  });

  const { channelId } = useParams<FASTChannelRoutesParams>();

  const queryClient = useQueryClient();

  const getSchedulesQueryKey = FastAPI.generateQuery({
    fn: FastAPI.getSchedules,
    params: [
      {
        channelId,
      },
    ],
  }).queryKey;

  const { mutate: updateSchedule } = useMutation({
    mutationFn: async (updatedScheduleList: UpdatedSchedule[]) =>
      FastAPI.updateSchedules({
        channelId,
        updatedScheduleList,
      }),
    onSuccess: () => {
      toast.success(t("saveSuccess", { ns: "common" }));
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
    onSettled: () => {
      queryClient.invalidateQueries(getSchedulesQueryKey);
    },
  });

  const handleFormSubmit: SubmitHandler<SchedulesSettingsFieldValues> = async (
    updatedSchedulesById,
  ) => {
    const updatedScheduleList: UpdatedSchedule[] = Object.values(
      updatedSchedulesById,
    ).map((schedule) => ({
      uuid: schedule.uuid,
      name: schedule.name,
      color: schedule.color,
      daily_ads_objective: schedule.daily_ads_objective * 3600,
      daily_programs_objective: schedule.daily_programs_objective * 3600,
    }));

    updateSchedule(updatedScheduleList);
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <form
        className="flex flex-col gap-[0.78125rem] pb-[0.78125rem]"
        id={formId}
        onSubmit={methods.handleSubmit((data) => handleFormSubmit(data))}
      >
        {scheduleList.map((schedule) => (
          <ScheduleSettings
            key={schedule.uuid}
            isDefaultSchedule={schedule.default}
            uuid={schedule.uuid}
          />
        ))}
      </form>
    </FormProvider>
  );
}
